import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  TablePagination,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DocuwareTemplateService from '../../services/DocuwareTemplateService';
import notify from '../../utils/notifier';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      width: '90%',
      maxWidth: '1000px',
      height: '80vh',
      maxHeight: '800px',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchBox: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
  },
  table: {
    minWidth: 650,
  },
  fileIcon: {
    marginRight: theme.spacing(1),
    color: '#d32f2f',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  noDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    color: theme.palette.text.secondary,
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
  },
  documentRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const DocumentSelectionModal = ({ open, onClose, onSelect, currentCompany }) => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // Tab configuration
  const tabs = [
    { label: 'Search', value: 'search' },
    { label: 'Folders', value: 'folders' }
  ];

  // Fetch folders/document types
  const fetchFolders = async () => {
    try {
      const response = await DocuwareTemplateService.getAllTemplates();
      if (response && response.data) {
        let templateResponse = response.data ? response.data.documentTypes : [];
        const folderNames = templateResponse.map(template => template.name);
        setFolders(folderNames);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      notify({
        type: 'error',
        message: 'Failed to load folders'
      });
    }
  };

  // Fetch documents based on current tab and filters
  const fetchDocuments = async (tabType = tabs[selectedTab].value, searchTerm = searchQuery, pageNum = page, pageSize = rowsPerPage, folder = selectedFolder) => {
    try {
      setLoading(true);
      
      let queryParams = {
        '_page': pageNum + 1,
        '_limit': pageSize
      };

      if (searchTerm && searchTerm.trim()) {
        queryParams['q'] = searchTerm.trim();
      }

      // Add filters based on tab type
      switch (tabType) {
        case 'search':
          // For search, use general document search
          queryParams['_documentType'] = 'other';
          break;
        case 'folders':
          // For folders, filter by selected folder/document type
          if (folder) {
            queryParams['_documentType'] = folder.toLowerCase();
          } else {
            // If no folder selected in folders tab, don't fetch anything
            setDocuments([]);
            setTotalCount(0);
            setLoading(false);
            return;
          }
          break;
        default:
          queryParams['_documentType'] = 'other';
      }
      
      let requestObj = {}; // Empty request body for searchDocuments

      const response = await DocuwareTemplateService.searchDocuments(requestObj, queryParams);
      
      if (response && response.data) {
        const documents = response.data || [];
        const totalCount = parseInt(response.headers?.['x-total-count'] || documents.length);
        
        setDocuments(documents);
        setTotalCount(totalCount);
        
        if (!documents.length && tabType === 'folders' && folder) {
          notify({
            type: 'info',
            message: `No documents found in folder "${folder}"`
          });
        }
      } else {
        setDocuments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      notify({
        type: 'error',
        message: 'Failed to load documents. Please try again.'
      });
      setDocuments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data when modal opens
  useEffect(() => {
    if (open) {
      setSelectedDocument(null);
      setPage(0);
      setSearchQuery('');
      setSelectedFolder(null);
      
      // Fetch folders when modal opens
      if (folders.length === 0) {
        fetchFolders();
      }
      
      // Only fetch documents for search tab initially
      if (selectedTab === 0) { // Search tab
        fetchDocuments();
      }
    }
  }, [open, selectedTab]);

  // Handle search
  useEffect(() => {
    if (open && (selectedTab === 0 || (selectedTab === 1 && selectedFolder))) {
      const debounceTimer = setTimeout(() => {
        setPage(0);
        fetchDocuments(tabs[selectedTab].value, searchQuery, 0, rowsPerPage, selectedFolder);
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [searchQuery, selectedFolder]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(0);
    setSelectedDocument(null);
    setSearchQuery('');
    if (newValue === 1) { // Folders tab
      setSelectedFolder(null);
    }
  };

  // Handle folder selection
  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setPage(0);
    setSelectedDocument(null);
    setSearchQuery('');
    // Fetch documents for the selected folder
    fetchDocuments('folders', '', 0, rowsPerPage, folder);
  };

  // Handle document selection
  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchDocuments(tabs[selectedTab].value, searchQuery, newPage, rowsPerPage, selectedFolder);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchDocuments(tabs[selectedTab].value, searchQuery, 0, newRowsPerPage, selectedFolder);
  };

  // Handle confirm selection
  const handleConfirmSelection = () => {
    if (selectedDocument) {
      onSelect(selectedDocument);
      handleClose();
    }
  };

  // Handle close
  const handleClose = () => {
    setSelectedDocument(null);
    setSearchQuery('');
    setSelectedFolder(null);
    setPage(0);
    onClose();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth={false}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">Select Document from Suite</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Search Box */}
        <Box className={classes.searchBox}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs */}
        <Box className={classes.tabsContainer}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        {/* Folder Selection (only show on Folders tab) */}
        {selectedTab === 1 && (
          <Box style={{ padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" gutterBottom>
              Select a Folder:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {folders.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Loading folders...
                </Typography>
              ) : (
                folders.map((folder) => (
                  <Button
                    key={folder}
                    variant={selectedFolder === folder ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleFolderSelect(folder)}
                    style={{
                      margin: '4px',
                      textTransform: 'none',
                      backgroundColor: selectedFolder === folder ? '#1976d2' : 'transparent',
                      color: selectedFolder === folder ? 'white' : '#1976d2',
                    }}
                  >
                    {folder}
                  </Button>
                ))
              )}
            </Box>
            {selectedFolder && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                Showing documents from: {selectedFolder}
              </Typography>
            )}
          </Box>
        )}

        {/* Document Table */}
        <Box className={classes.tableContainer}>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Box className={classes.noDataContainer}>
              <Typography variant="h6" gutterBottom>
                {selectedTab === 1 && !selectedFolder ? 'Select a Folder' : 'No documents found'}
              </Typography>
              <Typography variant="body2">
                {selectedTab === 1 && !selectedFolder 
                  ? 'Please select a folder to view its documents' 
                  : searchQuery 
                    ? 'Try adjusting your search criteria' 
                    : selectedFolder 
                      ? `No documents available in folder "${selectedFolder}"` 
                      : 'No documents available in this section'
                }
              </Typography>
            </Box>
          ) : (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Modified Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => {
                  const isSelected = selectedDocument?.id === doc.id;
                  return (
                    <TableRow
                      key={doc.id}
                      className={`${classes.documentRow} ${isSelected ? classes.selectedRow : ''}`}
                      onClick={() => handleDocumentSelect(doc)}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          checked={isSelected}
                          onChange={() => handleDocumentSelect(doc)}
                          value={doc.id}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PictureAsPdfIcon className={classes.fileIcon} />
                          <Typography variant="body2">
                            {doc.fileName || doc.name || 'Untitled Document'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.fileType || 'PDF'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFileSize(doc.fileSize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(doc.modifiedDate || doc.createdDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>

        {/* Pagination */}
        {documents.length > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          color="primary"
          disabled={!selectedDocument}
        >
          Select Document
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentSelectionModal;